/* ============================================================
   PSM native B2B quantity rules + volume pricing.
   - <psm-volume-pricing> hydrates the PDP tier table: live unit
     price, active-tier highlight, re-render on variant change,
     and snaps the qty input to a valid value (min + k*increment)
     so Shopify's B2B validation never errors.
   - window.PSMQtyRules.snap() is the shared rounding helper, also
     used by assets/cart-items.js for the cart quantity boxes.
   Data source: <script id="psm-qty-data-{section}"> emitted by
   sections/main-product.liquid (per-variant rule + breaks, money
   preformatted in Liquid).
   ============================================================ */
(function () {
  "use strict";

  /**
   * Round a quantity UP to the next valid value for a quantity rule.
   * Valid values are min, min+inc, min+2*inc, … (clamped to max).
   * Rounding up (never below what was typed) keeps the value Shopify-valid so
   * a B2B catalog never rejects the add-to-cart.
   * @param {number|string} value
   * @param {{min:number,inc:number,max:?number}} rule
   * @param {boolean} allowZero - cart inputs may go to 0 (remove line)
   */
  function snap(value, rule, allowZero) {
    var v = parseInt(value, 10);
    var min = rule.min || 1;
    var inc = rule.inc || 1;
    if (isNaN(v)) v = min;
    if (allowZero && v <= 0) return 0;
    if (v < min) return min;
    var k = Math.ceil((v - min) / inc);
    v = min + k * inc;
    if (v < min) v = min;
    if (rule.max != null && v > rule.max) {
      v = min + Math.floor((rule.max - min) / inc) * inc;
      if (v < min) v = min; // guard a misconfigured max < min (would emit a sub-minimum qty)
    }
    return v;
  }
  window.PSMQtyRules = { snap: snap };

  if (!customElements.get("psm-volume-pricing")) {
    customElements.define("psm-volume-pricing", class extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest(".product-info") || this.closest(".product");
        this.data = this.loadData();
        this.priceEl = this.productInfo &&
          this.productInfo.querySelector(".product-info__price .price__current");
        this.input = this.productInfo && (
          this.productInfo.querySelector('.product-info__add-to-cart .qty-input__input') ||
          this.productInfo.querySelector('input[name="quantity"]'));
        this.ruleLineEl = this.productInfo && this.productInfo.querySelector(".psm-qty-rule");
        this.flashEl = this.productInfo && this.productInfo.querySelector(".psm-qty-flash");
        this.currentVariantId = this.detectVariant();

        if (this.input) {
          this._onQty = this.onQtyChange.bind(this);
          this.input.addEventListener("change", this._onQty);
          // Enter in a qty field should commit/round the value, NOT submit the
          // product form. Submitting on Enter fires add-to-cart with the typed
          // (possibly invalid) value, which pops the cart loading overlay — and a
          // B2B catalog rejection leaves it spinning. Round up + flash instead.
          this.input.addEventListener("keydown", function (e) {
            if (e.key === "Enter" || e.keyCode === 13) {
              e.preventDefault();
              e.stopImmediatePropagation(); // keep Enter from reaching the form / media gallery
              this.snapInput();
            }
          }.bind(this), true);
          // Safety net: snap once more right before the add-to-cart form submits, in
          // capture phase so the corrected value reaches the request even if the
          // button is clicked before the change event settles.
          this.form = this.input.closest("form");
          if (this.form) this.form.addEventListener("submit", this.onFormSubmit.bind(this), true);
        }
        var picker = this.productInfo && this.productInfo.querySelector("variant-picker");
        if (picker) picker.addEventListener("on:variant:change", this.onVariantChange.bind(this));

        this.refresh();
        this.updateRuleLine();
      }

      loadData() {
        var el = document.getElementById("psm-qty-data-" + this.dataset.section);
        if (!el) return {};
        try { return JSON.parse(el.textContent); } catch (e) { return {}; }
      }

      detectVariant() {
        var idInput = this.productInfo && this.productInfo.querySelector('input[name="id"]');
        if (idInput && idInput.value) return String(idInput.value);
        var keys = Object.keys(this.data);
        return keys.length ? keys[0] : null;
      }

      vd() { return this.data[this.currentVariantId] || null; }

      onVariantChange(evt) {
        var v = evt.detail && evt.detail.variant;
        if (!v) return;
        this.currentVariantId = String(v.id);
        var vd = this.vd();
        if (vd && this.input) {
          this.input.dataset.ruleMin = vd.rule.min;
          this.input.dataset.ruleStep = vd.rule.inc;
          this.input.min = vd.rule.min;
          this.input.step = vd.rule.inc;
          if (vd.rule.max != null) {
            this.input.max = vd.rule.max;
            this.input.dataset.ruleMax = vd.rule.max;
          } else {
            this.input.removeAttribute("max");
            delete this.input.dataset.ruleMax;
          }
          this.input.value = snap(this.input.value, vd.rule, false);
        }
        this.renderTable();
        this.refresh();
        this.updateRuleLine();
      }

      onQtyChange() { this.snapInput(); this.refresh(); }

      onFormSubmit() { this.snapInput(); }

      /* Snap the input to a valid (rounded-up) quantity; flash a notice when the
         typed value was bumped up. Shared by the change event and the submit guard. */
      snapInput() {
        var vd = this.vd();
        if (!vd || !this.input) return;
        var typed = parseInt(this.input.value, 10);
        var snapped = snap(this.input.value, vd.rule, false);
        if (String(snapped) !== String(this.input.value)) {
          this.input.value = snapped;
          if (!isNaN(typed) && snapped > typed) this.flashRounded(snapped);
        }
      }

      flashRounded(val) {
        if (!this.flashEl) return;
        this.flashEl.textContent = "Quantity rounded up to " + val;
        this.flashEl.classList.remove("is-flashing");
        void this.flashEl.offsetWidth; // restart the animation
        this.flashEl.classList.add("is-flashing");
        clearTimeout(this._flashT);
        var el = this.flashEl;
        this._flashT = setTimeout(function () { el.classList.remove("is-flashing"); }, 3600);
      }

      activeTierPrice(vd, qty) {
        var price = vd.base;
        for (var i = 0; i < vd.breaks.length; i++) {
          if (qty >= vd.breaks[i].min) price = vd.breaks[i].price;
        }
        return price;
      }

      refresh() {
        var vd = this.vd();
        if (!vd) return;
        var qty = this.input ? (parseInt(this.input.value, 10) || vd.rule.min) : vd.rule.min;
        if (this.priceEl) this.priceEl.textContent = this.activeTierPrice(vd, qty);
        this.querySelectorAll(".psm-vp-row").forEach(function (row) {
          var from = parseInt(row.dataset.from, 10);
          var to = row.dataset.to ? parseInt(row.dataset.to, 10) : Infinity;
          row.classList.toggle("psm-vp-row--active", qty >= from && qty <= to);
        });
      }

      renderTable() {
        var vd = this.vd();
        if (!vd) { this.innerHTML = ""; return; }
        var quoteUrl = this.dataset.quoteUrl || "/pages/project-enquiry";

        function row(from, to, priceCur, save) {
          var label = to == null ? from + "+" : from + "–" + to;
          var saveCell = save == null
            ? '<td class="psm-vp-save">—</td>'
            : '<td class="psm-vp-save psm-vp-save--on">Save ' + save + '%</td>';
          return '<tr class="psm-vp-row" data-from="' + from + '" data-to="' +
            (to == null ? "" : to) + '"><td class="psm-vp-qty">' + label +
            '</td><td class="psm-vp-price">' + priceCur + "</td>" + saveCell + "</tr>";
        }
        function pctSaved(base, tier) {
          return Math.round((base - tier) / base * 100);
        }

        var html = "";
        if (vd.breaks.length) {
          var topMin = vd.breaks[vd.breaks.length - 1].min;
          html += '<div class="psm-vp-card"><div class="psm-vp-head">' +
            '<span class="psm-vp-title">' +
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 16V8l-9-5-9 5v8l9 5 9-5z"/><path d="m3.3 8.3 8.7 4.8 8.7-4.8M12 22V13"/></svg> Volume pricing</span>' +
            '<a class="psm-vp-quote" href="' + quoteUrl + '">Need ' + topMin + '+? Get a quote →</a>' +
            '</div><table class="psm-vp-table" aria-label="Volume pricing">' +
            '<thead><tr><th class="psm-vp-qty">Quantity</th>' +
            '<th class="psm-vp-price">Price / unit</th>' +
            '<th class="psm-vp-save">Qty Discount</th></tr></thead><tbody>';
          html += row(vd.rule.min, vd.breaks[0].min - 1, vd.baseCur, null);
          for (var i = 0; i < vd.breaks.length; i++) {
            var last = i === vd.breaks.length - 1;
            html += row(vd.breaks[i].min, last ? null : vd.breaks[i + 1].min - 1,
              vd.breaks[i].priceCur, pctSaved(vd.baseCents, vd.breaks[i].cents));
          }
          html += "</tbody></table></div>";
        }
        this.innerHTML = html;
      }

      /* The rule line lives below the qty box (snippets/psm-qty-rule.liquid), not
         inside this element. Keep the "pack tag" in sync with the active variant's rule.
         Segments render only when meaningful (inc/min > 1), so the divider (.seg + .seg)
         appears only when both are present. Empties the <p> so :empty hides it. */
      updateRuleLine() {
        if (!this.ruleLineEl) return;
        var vd = this.vd();
        var inc = vd && vd.rule ? vd.rule.inc : 0;
        var min = vd && vd.rule ? vd.rule.min : 0;
        var segs = "";
        if (inc > 1) segs += '<span class="psm-qty-tag__seg psm-qty-tag__seg--pack">Packs of&nbsp;<span class="psm-qty-tag__q">' + inc + "</span></span>";
        if (min > 1) segs += '<span class="psm-qty-tag__seg psm-qty-tag__seg--min"><span class="psm-qty-tag__lbl">min</span>&nbsp;<span class="psm-qty-tag__q">' + min + "</span></span>";
        if (!segs) { this.ruleLineEl.textContent = ""; return; }
        var icon = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8 12 3 3 8l9 5 9-5Z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg>';
        this.ruleLineEl.innerHTML = '<span class="psm-qty-tag"><span class="psm-qty-tag__ic" aria-hidden="true">' + icon + "</span>" + segs + "</span>";
      }
    });
  }
})();
